import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle } from "@/components/common/SectionTitle";
import { useLanguage } from "@/context/LanguageContext";
import { getSiteContent } from "@/data/siteContent";

export default function AboutPage() {
  const { language, isArabic } = useLanguage();
  const content = getSiteContent(language);

  return (
    <section className="py-16 md:py-24" data-testid="about-page">
      <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
        <div className="rounded-3xl bg-slate-900 p-6 md:p-10" data-testid="about-hero-block">
          <SectionTitle
            badge={isArabic ? "من نحن" : "About Root Trade"}
            title={content.about.title}
            description={isArabic ? "شريككم الموثوق في توريد مواد النظافة والضيافة" : "Your trusted partner for hygiene and hospitality procurement"}
            testIdPrefix="about-main"
            inverse
          />
          <div className="mx-auto grid max-w-5xl gap-4 md:grid-cols-2" data-testid="about-paragraphs-grid">
            <p className="rounded-2xl bg-white/10 p-5 text-sm text-slate-200 md:text-base" data-testid="about-summary-paragraph-1">
              {content.about.summary}
            </p>
            <p className="rounded-2xl bg-white/10 p-5 text-sm text-slate-200 md:text-base" data-testid="about-summary-paragraph-2">
              {content.about.summary2}
            </p>
          </div>
          <div className="mx-auto mt-4 max-w-5xl rounded-2xl bg-secondary/15 p-5" data-testid="about-mission-block">
            <h3 className="mb-2 text-xl font-bold text-white" data-testid="about-mission-title">
              {content.about.missionTitle}
            </h3>
            <p className="text-sm text-slate-100 md:text-base" data-testid="about-mission-text">
              {content.about.missionText}
            </p>
          </div>
          <div className="mx-auto mt-4 max-w-5xl rounded-2xl bg-primary/40 p-5" data-testid="about-vision-2030-block">
            <h3 className="mb-2 text-xl font-bold text-white" data-testid="about-vision-2030-title">
              {content.about.vision2030Title}
            </h3>
            <p className="mb-3 text-sm text-slate-100 md:text-base" data-testid="about-vision-2030-text">
              {content.about.vision2030Text}
            </p>
            <ul className="space-y-2" data-testid="about-vision-2030-points-list">
              {content.about.vision2030Points.map((point, index) => (
                <li key={`vision-2030-point-${index}`} className="text-sm text-slate-100 md:text-base" data-testid={`about-vision-2030-point-${index}`}>
                  • {point}
                </li>
              ))}
            </ul>
          </div>
          <div className="mx-auto mt-4 max-w-5xl rounded-2xl bg-white/10 p-5" data-testid="about-vision-block">
            <h3 className="mb-2 text-xl font-bold text-white" data-testid="about-vision-title">
              {content.about.visionTitle}
            </h3>
            <p className="text-sm text-slate-100 md:text-base" data-testid="about-vision-text">
              {content.about.visionText}
            </p>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="about-values-grid">
          {content.about.cards.map((card, index) => {
            const Icon = card.icon;

            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.1 }}
              >
                <Card className="h-full border-slate-200 shadow-sm" data-testid={`about-value-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/15 text-secondary" data-testid={`about-value-icon-wrap-${index}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mb-2 text-xl font-bold text-slate-900" data-testid={`about-value-title-${index}`}>
                      {card.title}
                    </h3>
                    <p className="text-sm text-slate-600" data-testid={`about-value-description-${index}`}>
                      {card.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
